import { Link } from "react-router-dom";
import { ArrowRight, Award, Leaf, ShieldCheck } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Seo } from "@/components/Seo";
import { IMG } from "@/data/content";

const values = [
  {
    icon: Award,
    title: "Craft over speed",
    desc: "We book fewer jobs so every floor gets the hours it deserves.",
  },
  {
    icon: Leaf,
    title: "Honest materials",
    desc: "FSC-certified timber and low-VOC finishes as standard, never an upgrade.",
  },
  {
    icon: ShieldCheck,
    title: "Guaranteed work",
    desc: "Every installation carries a written workmanship guarantee.",
  },
];

const milestones = [
  ["The beginning", "A one-van operation laying parquet across London terraces."],
  ["The craft deepens", "Dustless refinishing and heritage restoration join the toolkit."],
  ["Commercial trust", "Studios, galleries and offices begin specifying COC by name."],
  ["Today", "1,200+ floors later — still measured, cut and laid by our own hands."],
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-20">
      <Seo
        title="About COC Flooring Ltd — Master Flooring Craftsmen"
        description="Fitters, sanders and tilers, not salespeople. Learn how COC Flooring Ltd approaches every floor, from survey to hand-finished edges."
      />
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <MaskedLine delay={0.1}>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary font-semibold">
                About COC
              </p>
            </MaskedLine>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              <MaskedLine delay={0.2}>Built from</MaskedLine>
              <MaskedLine delay={0.32}>
                the <em className="italic font-light text-primary">ground</em> up.
              </MaskedLine>
            </h1>
            <MaskedLine delay={0.45}>
              <p className="mt-6 text-muted-foreground font-light text-base sm:text-lg leading-relaxed max-w-xl">
                COC Flooring Ltd began with a simple conviction: a floor is not
                a surface, it is the foundation of how a room feels. Two decades
                later, that conviction still guides every plank we lay.
              </p>
            </MaskedLine>
            <MaskedLine delay={0.6}>
              <p className="mt-4 text-muted-foreground font-light leading-relaxed max-w-xl">
                We are fitters, sanders and tilers — not salespeople. The person
                who surveys your home is the person who finishes your floor.
              </p>
            </MaskedLine>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="overflow-hidden rounded-2xl aspect-[4/5] max-h-[80vh]">
                  <img
                    src={IMG.craftsman}
                    alt="COC craftsman at work"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-[#181715] text-[#FAF8F5] rounded-xl px-7 py-5 shadow-xl">
                  <div className="font-serif text-3xl text-[#E9C093]">20+</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mt-1">
                    Years at the craft
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              What we refuse to compromise
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.12}>
                <div
                  data-testid={`value-card-${i}`}
                  className="bg-card border border-border rounded-xl p-8 h-full hover:border-primary/50 transition-colors duration-500"
                >
                  <v.icon size={26} className="text-primary" />
                  <h3 className="mt-6 font-serif text-xl">{v.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground font-light leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#181715] text-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              The story so far
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map(([title, desc], i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div className="border-t-2 border-[#C87D32]/40 pt-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C87D32]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-3 font-serif text-xl">{title}</h3>
                  <p className="mt-3 text-sm text-[#A39E93] font-light leading-relaxed">
                    {desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="mt-14 inline-flex items-center gap-3 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full hover:bg-[#9A5B1D] transition-colors duration-300"
            >
              Start your project <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
